import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, useAuth } from '../App';

const STEPS = [
  { key: 'demo', title: 'Load the demo market', route: '/prove-it', target: '[data-testid="demo-pack-load-btn"]', doneWhen: '[data-testid="demo-pack-ready"]',
    body: 'Start with data on the screen. This seeds 12 sample homeowners, 8 verified callable contacts and a signed proposal — all labelled demo.' },
  { key: 'scan', title: 'Run a live permit scan', route: '/scanner', target: '[data-testid="run-scan-btn"]',
    body: 'Now pull real leads. Type a market like “Philadelphia” or “Brooklyn” (or tap a quick city) and hit Find Leads — every result is a real permit pulled live, scored for solar fit.' },
  { key: 'enrich', title: 'Verify a homeowner contact', route: (ctx) => ctx.topLeadId ? `/leads/${ctx.topLeadId}` : '/leads', target: '[data-testid="waterfall-btn"]',
    body: 'Open a lead and press OSINT Waterfall. The engine chains public records and a deep-contact lookup, then only marks a phone “callable” when it is a confirmed mobile.' },
  { key: 'prove', title: 'Run the Prove-It report', route: '/prove-it', target: '[data-testid="run-proof-btn"]',
    body: 'One click scans a live market, verifies the top owners and prices the pipeline against the monthly rental — the investor ROI case in about two minutes.' },
  { key: 'proposal', title: 'Open the finished proposal', route: '/prove-it', target: '[data-testid="demo-pack-open-proposal"]',
    body: 'Finish by opening the signed demo proposal: interactive web view, satellite roof render and the 13-page PDF the homeowner received.' },
];

const key = (email) => `vs_tour_${email}`;

export default function InvestorTour() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const email = user?.email;
  const [state, setState] = useState(() => {
    try { return JSON.parse(localStorage.getItem(key(email)) || 'null'); } catch { return null; }
  });
  const [ctx, setCtx] = useState({});
  const [rect, setRect] = useState(null);
  const [hit, setHit] = useState(false);
  const stepIdx = state?.step ?? 0;
  const step = STEPS[stepIdx];
  const active = user?.role === 'investor' && !user?.tour_done && !(state?.done) && !(state?.minimized);

  const persist = useCallback((next) => {
    setState(next);
    localStorage.setItem(key(email), JSON.stringify(next));
  }, [email]);

  useEffect(() => { if (!state && email) persist({ step: 0, done: false }); }, [state, email, persist]);
  useEffect(() => {
    if (!active) return;
    api.get('/api/demo/status').then(r => setCtx({ topLeadId: r.data.top_lead_id, proposalId: r.data.proposal?.id })).catch(() => {});
  }, [active, stepIdx, location.pathname]);

  const route = typeof step?.route === 'function' ? step.route(ctx) : step?.route;
  const onRoute = route && location.pathname === route;

  useEffect(() => {
    if (!active || !step) return undefined;
    const tick = () => {
      const el = onRoute ? document.querySelector(step.target) : null;
      setRect(el ? el.getBoundingClientRect() : null);
      if (step.doneWhen && onRoute && document.querySelector(step.doneWhen)) setHit(true);
    };
    tick();
    const id = setInterval(tick, 500);
    window.addEventListener('resize', tick);
    return () => { clearInterval(id); window.removeEventListener('resize', tick); };
  }, [active, step, onRoute, location.pathname]);

  useEffect(() => {
    if (!active || !step) return undefined;
    const h = (e) => { if (e.target.closest && e.target.closest(step.target)) setHit(true); };
    document.addEventListener('click', h, true);
    return () => document.removeEventListener('click', h, true);
  }, [active, step]);

  const advanceRef = useRef(null);
  useEffect(() => {
    if (!hit) return undefined;
    advanceRef.current = setTimeout(() => next(), 1400);
    return () => clearTimeout(advanceRef.current);
  }, [hit]); // eslint-disable-line

  const finish = async () => {
    persist({ ...state, done: true });
    try { await api.put('/api/me/tour', { done: true, step: STEPS.length }); } catch {}
    const saved = JSON.parse(localStorage.getItem('sls_user') || '{}');
    localStorage.setItem('sls_user', JSON.stringify({ ...saved, tour_done: true }));
  };

  const next = () => {
    setHit(false);
    if (stepIdx >= STEPS.length - 1) return finish();
    persist({ ...state, step: stepIdx + 1 });
    api.put('/api/me/tour', { done: false, step: stepIdx + 1 }).catch(() => {});
  };

  if (!active || !step) return null;

  return (
    <>
      {rect && (
        <div className="fixed z-[70] pointer-events-none rounded-xl transition-all duration-300"
             style={{ top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12,
                      boxShadow: '0 0 0 3px #FAAB1C, 0 0 0 9999px rgba(12,36,23,0.45)' }} data-testid="tour-spotlight" />
      )}
      <div className="fixed z-[80] bottom-5 right-5 w-[360px] max-w-[calc(100vw-2rem)] rounded-2xl bg-[#0C2417] text-white shadow-2xl border border-white/10 overflow-hidden" data-testid="investor-tour">
        <div className="h-1 bg-white/10"><div className="h-full transition-all duration-500" style={{ width: `${((stepIdx + (hit ? 1 : 0)) / STEPS.length) * 100}%`, background: '#FAAB1C' }} /></div>
        <div className="p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold uppercase tracking-widest text-amber-300"><i className="fa-solid fa-route mr-1.5" />Investor walkthrough · {stepIdx + 1} / {STEPS.length}</span>
            <button onClick={finish} className="text-white/50 hover:text-white text-xs" data-testid="tour-end-btn">End tour</button>
          </div>
          <h4 className="font-heading text-lg font-bold leading-tight">{step.title}</h4>
          <p className="text-sm text-slate-300 mt-2 leading-relaxed">{step.body}</p>
          {hit && <p className="mt-3 text-sm font-bold text-emerald-400" data-testid="tour-step-done"><i className="fa-solid fa-circle-check mr-1.5" />Done — moving on…</p>}
          <div className="mt-4 flex items-center gap-2">
            {!onRoute ? (
              <button onClick={() => navigate(route)} className="flex-1 px-4 py-2.5 rounded-xl text-sm font-bold text-[#0C2417]" style={{ background: '#FAAB1C' }} data-testid="tour-go-btn"><i className="fa-solid fa-location-arrow mr-1.5" />Take me there</button>
            ) : (
              <span className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold bg-white/5 text-slate-300 border border-white/10"><i className="fa-solid fa-hand-pointer mr-1.5 text-amber-300" />{rect ? 'Click the highlighted button' : 'Waiting for the page to load…'}</span>
            )}
            <button onClick={next} className="px-3 py-2.5 rounded-xl text-xs font-semibold text-white/70 hover:text-white hover:bg-white/5" data-testid="tour-skip-btn">Skip →</button>
          </div>
        </div>
      </div>
    </>
  );
}
